import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Globe, Trash2, Eye, Loader2, PlusCircle, Calendar, Image as ImageIcon, Heart,
  Search, History, ChevronDown,
} from 'lucide-react';
import Sidebar, { SidebarProvider } from '../../components/navigation/Sidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';



import {
  getCopyBlogsSources,
  addCopyBlogsSource,
  deleteCopyBlogsSource,
} from '../../services/wordpressService';

import {
  fetchArticlesFast,
  getWebsiteLogo,
  normalizeWebsiteUrl,
  validateArticleForSave
} from '../../services/articleScraperService';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../../components/ui/ModalProvider';
import { useLanguageProfile } from '../../contexts/LanguageProfileContext';
import { useRequestGuard } from '../../hooks/useRequestGuard';
import { getCopyBlogsQueueKey } from '../../utils/copyBlogsQueue';
import {
  MGMT_STATUSES,
  loadMgmtStatusMap,
  saveMgmtStatus,
  loadLastViewedMap,
  recordLastViewed,
  loadStatsCache,
  saveStatsEntry,
} from '../../utils/copyBlogsSourceMeta';

// Per-article image cache
const _ogCache = {};

function parseOgImage(html) {
  const m =
    html.match(/<meta[^>]+property=["']og:image["'][^>]+content=["']([^"']+)["']/i) ||
    html.match(/<meta[^>]+content=["']([^"']+)["'][^>]+property=["']og:image["']/i) ||
    html.match(/<meta[^>]+name=["']twitter:image["'][^>]+content=["']([^"']+)["']/i);
  if (!m?.[1]) return null;
  const url = m[1].trim();
  return url.startsWith('//') ? 'https:' + url : url;
}

// 3-tier fetch: 1) WP oEmbed (no proxy, CORS-allowed)  2) two CORS proxies raced
async function fetchOgImage(articleUrl) {
  if (_ogCache[articleUrl]) return _ogCache[articleUrl];

  try {
    const origin = new URL(articleUrl).origin;

    // Tier 1: WP oEmbed API — same-origin WP endpoint, CORS always allowed, returns thumbnail_url
    try {
      const ctrl = new AbortController();
      const tid = setTimeout(() => ctrl.abort(), 3000);
      const res = await fetch(
        `${origin}/wp-json/oembed/1.0/embed?url=${encodeURIComponent(articleUrl)}&format=json`,
        { signal: ctrl.signal }
      );
      clearTimeout(tid);
      if (res.ok) {
        const data = await res.json();
        if (data?.thumbnail_url) {
          _ogCache[articleUrl] = data.thumbnail_url;
          return data.thumbnail_url;
        }
      }
    } catch {}

    // Tier 2: Race two CORS proxies for og:image from page HTML
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), 7000);
    const html = await Promise.any([
      `https://api.allorigins.win/raw?url=${encodeURIComponent(articleUrl)}`,
      `https://corsproxy.io/?${encodeURIComponent(articleUrl)}`,
    ].map(proxy =>
      fetch(proxy, { signal: ctrl.signal }).then(r => r.ok ? r.text() : Promise.reject())
    ));
    clearTimeout(tid);

    const url = parseOgImage(html);
    if (url) {
      _ogCache[articleUrl] = url;
      return url;
    }
  } catch {}

  return null;
}

function ArticleThumbnail({ article }) {
  const initial = article?.featuredImageUrl || _ogCache[article?.articleUrl] || null;
  const [imgSrc, setImgSrc] = useState(initial);
  const [imgError, setImgError] = useState(false);
  const [loading, setLoading] = useState(!initial && !!article?.articleUrl);
  const containerRef = useRef(null);
  const tried = useRef(!!initial || !article?.articleUrl);

  useEffect(() => {
    if (tried.current) return;
    const el = containerRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      obs.disconnect();
      if (tried.current) return;
      tried.current = true;

      fetchOgImage(article.articleUrl)
        .then(url => { if (url) setImgSrc(url); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, { rootMargin: '200px' });

    obs.observe(el);
    return () => obs.disconnect();
  }, [article?.articleUrl]);

  return (
    <div ref={containerRef} className="absolute inset-0">
      {imgSrc && !imgError ? (
        <img
          src={imgSrc}
          alt={article?.title}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          onError={() => { setImgSrc(null); setImgError(true); setLoading(false); }}
        />
      ) : loading ? (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
          <ImageIcon className="w-10 h-10 text-slate-300" />
        </div>
      )}
    </div>
  );
}

// Per-source cache: websiteUrl → { articles, page, hasMore, fetchedAt }
const _articlesCache = {};
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const FETCH_LIMIT  = 20;

function sortByDate(articles) {
  return [...articles].sort(
    (a, b) => new Date(b.publishDate || 0) - new Date(a.publishDate || 0)
  );
}

// Article Side Panel Component — no DB, fetches directly from the website
function ArticlesPanel({ source, isOpen, onClose, onAddToAutomation, onArticlesLoaded }) {
  const { alert } = useModal();
  const [allArticles, setAllArticles]   = useState([]);
  const [fetchPage, setFetchPage]       = useState(1);
  const [hasMore, setHasMore]           = useState(false);
  const [loading, setLoading]           = useState(false);
  const [loadingMore, setLoadingMore]   = useState(false);
  const [addedUrls, setAddedUrls]       = useState(new Set());
  const [savingAll, setSavingAll]       = useState(false);
  const panelRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (panelRef?.current && !panelRef?.current?.contains(event?.target)) onClose();
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Load when panel opens (use cache if fresh)
  useEffect(() => {
    if (isOpen && source) loadArticles(false);
  }, [isOpen, source]);

  const loadArticles = async (forceRefresh = false) => {
    const cacheKey = source?.websiteUrl;
    const cached   = _articlesCache[cacheKey];

    if (!forceRefresh && cached && (Date.now() - cached.fetchedAt) < CACHE_TTL_MS) {
      setAllArticles(cached.articles);
      setFetchPage(cached.page || 1);
      setHasMore(cached.hasMore ?? false);
      return;
    }

    setLoading(true);
    setAllArticles([]);
    setFetchPage(1);
    setHasMore(false);
    try {
      const data = await fetchArticlesFast(source?.websiteUrl, FETCH_LIMIT, 1);
      const sorted = sortByDate(data || []);
      const more = (data || []).length === FETCH_LIMIT;
      _articlesCache[cacheKey] = { articles: sorted, page: 1, hasMore: more, fetchedAt: Date.now() };
      setAllArticles(sorted);
      setHasMore(more);
      onArticlesLoaded?.(source, {
        latestArticleDate: sorted?.[0]?.publishDate || null,
        articlesCount: sorted.length,
        hasMore: more,
        checkedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error('Error fetching articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreArticles = async () => {
    const nextPage = fetchPage + 1;
    setLoadingMore(true);
    try {
      const data = await fetchArticlesFast(source?.websiteUrl, FETCH_LIMIT, nextPage);
      if ((data || []).length > 0) {
        const seen = new Set();
        const combined = sortByDate([...allArticles, ...data]).filter(a => {
          if (seen.has(a.articleUrl)) return false;
          seen.add(a.articleUrl);
          return true;
        });
        const more = data.length === FETCH_LIMIT;
        _articlesCache[source?.websiteUrl] = {
          articles: combined,
          page: nextPage,
          hasMore: more,
          fetchedAt: _articlesCache[source?.websiteUrl]?.fetchedAt || Date.now(),
        };
        setAllArticles(combined);
        setFetchPage(nextPage);
        setHasMore(more);
        onArticlesLoaded?.(source, {
          latestArticleDate: combined?.[0]?.publishDate || null,
          articlesCount: combined.length,
          hasMore: more,
          checkedAt: new Date().toISOString(),
        });
      } else {
        setHasMore(false);
      }
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSaveAllForWP = async () => {
    const unsaved = displayArticles.filter(a => !a.isAddedToAutomation);
    if (unsaved.length === 0) {
      alert('All loaded articles are already saved for WP.');
      return;
    }
    setSavingAll(true);
    for (const article of unsaved) {
      await handleAddToAutomation(article);
    }
    setSavingAll(false);
  };

  const handleAddToAutomation = async (article) => {
    try {
      const validation = validateArticleForSave(article);
      if (!validation?.isValid) {
        alert('❌ Cannot save article:\n\n' + validation?.errors?.join('\n'));
        return;
      }
      setAddedUrls(prev => new Set([...prev, article.articleUrl]));
      await onAddToAutomation(article);
    } catch (error) {
      console.error('Error adding to automation:', error);
      alert(`Error: ${error?.message}`);
    }
  };

  const displayArticles = allArticles.map(a => ({
    ...a,
    isAddedToAutomation: addedUrls.has(a.articleUrl),
  }));

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed inset-y-0 right-0 w-full md:w-2/3 lg:w-1/2 bg-white shadow-2xl z-50 overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1 min-w-0 mr-3">
              <h2 className="text-lg sm:text-2xl font-bold truncate">{source?.websiteName}</h2>
              <p className="text-blue-100 text-xs sm:text-sm mt-1 truncate">{source?.websiteUrl}</p>
            </div>
            <button onClick={onClose} className="text-white hover:bg-blue-800 rounded-lg p-2 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex justify-between items-center">
            <div className="text-blue-100 text-sm">
              {loading ? 'Fetching...' : allArticles.length > 0
                ? `${allArticles.length} articles loaded`
                : 'No articles yet'}
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleSaveAllForWP}
                disabled={loading || savingAll || allArticles.length === 0}
                className="bg-green-500 hover:bg-green-400 text-white"
              >
                {savingAll
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                  : <><PlusCircle className="w-4 h-4 mr-2" />Save All for WP</>}
              </Button>
              <Button
                onClick={() => loadArticles(true)}
                disabled={loading}
                className="bg-white text-blue-600 hover:bg-blue-50"
              >
                {loading
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Fetching...</>
                  : <><Globe className="w-4 h-4 mr-2" />Fetch Latest</>}
              </Button>
            </div>
          </div>
        </div>

        {/* Articles List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="space-y-4 animate-pulse">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                  <div className="flex flex-col sm:flex-row">
                    <div className="sm:w-48 h-36 bg-gray-200 flex-shrink-0" />
                    <div className="flex-1 p-4 space-y-3">
                      <div className="h-6 bg-gray-200 rounded w-3/4" />
                      <div className="h-4 bg-gray-200 rounded w-full" />
                      <div className="h-4 bg-gray-200 rounded w-1/2" />
                      <div className="flex gap-2 pt-2">
                        <div className="h-8 bg-gray-200 rounded w-24" />
                        <div className="h-8 bg-gray-200 rounded w-24" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : allArticles.length === 0 ? (
            <div className="text-center py-16">
              <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Articles Found</h3>
              <p className="text-gray-600 mb-6">Click "Fetch Latest" to load blog posts from this website.</p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {displayArticles.map((article, idx) => (
                  <div
                    key={article?.articleUrl || idx}
                    className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all bg-white"
                  >
                    <div className="flex flex-col sm:flex-row">
                      <div className="sm:w-48 h-36 bg-gray-100 flex-shrink-0 relative overflow-hidden">
                        <ArticleThumbnail article={article} />
                      </div>
                      <div className="flex-1 p-4">
                        <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2 hover:text-blue-600 transition-colors">
                          {article?.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-3">
                          {article?.publishDate && (
                            <span className="flex items-center bg-gray-50 px-2 py-1 rounded-full">
                              <Calendar className="w-3 h-3 mr-1" />
                              {new Date(article?.publishDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <a
                            href={article?.articleUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all"
                          >
                            <Eye className="w-4 h-4 mr-2" />View
                          </a>
                          <Button
                            onClick={() => handleAddToAutomation(article)}
                            disabled={article?.isAddedToAutomation}
                            size="sm"
                            className={`${article?.isAddedToAutomation ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} transition-all`}
                          >
                            {article?.isAddedToAutomation ? (
                              <><svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Added</>
                            ) : (
                              <><PlusCircle className="w-4 h-4 mr-2" />Save for WP</>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {hasMore && (
                <div className="mt-6 text-center">
                  <Button
                    onClick={loadMoreArticles}
                    disabled={loadingMore}
                    variant="outline"
                    className="w-full max-w-md mx-auto border-2 hover:border-blue-600 hover:bg-blue-50 transition-all"
                  >
                    {loadingMore ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading more...</>
                    ) : (
                      <><svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>Show 20 More</>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Dashboard helpers — activity status, management status, formatting.
// None of this touches scraping/fetching; it only interprets data already
// produced by fetchArticlesFast (via the stats cache below).
// ---------------------------------------------------------------------------

const ACTIVE_DAYS = 14;       // published within this window → Active
const GETTING_OLD_DAYS = 60;  // published within this window → Getting Old
const STALE_ATTENTION_DAYS = 30; // not checked in this long → flag for attention
const STATS_TTL_MS = 3 * 60 * 60 * 1000; // 3 hours between background re-checks
const STATS_CONCURRENCY = 3;

function getActivityStatus(stats) {
  if (!stats || !stats.checkedAt) return 'unknown';
  if (!stats.latestArticleDate) return 'inactive';
  const days = (Date.now() - new Date(stats.latestArticleDate).getTime()) / 86400000;
  if (days <= ACTIVE_DAYS) return 'active';
  if (days <= GETTING_OLD_DAYS) return 'getting_old';
  return 'inactive';
}

function isNeedsAttention(stats, mgmtStatus) {
  const activity = getActivityStatus(stats);
  if (activity === 'inactive') return true;
  if (mgmtStatus === MGMT_STATUSES.REVIEW || mgmtStatus === MGMT_STATUSES.REMOVE_CANDIDATE) return true;
  if (stats?.checkedAt) {
    const daysSinceCheck = (Date.now() - new Date(stats.checkedAt).getTime()) / 86400000;
    if (daysSinceCheck > STALE_ATTENTION_DAYS) return true;
  }
  return false;
}

const ACTIVITY_META = {
  active:      { label: 'ACTIVE',       dot: 'bg-emerald-500',              badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  getting_old: { label: 'GETTING OLD',  dot: 'bg-amber-500',                badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  inactive:    { label: 'INACTIVE',     dot: 'bg-gray-400',                 badge: 'bg-gray-100 text-gray-500 border-gray-200' },
  unknown:     { label: 'CHECKING…',    dot: 'bg-blue-400 animate-pulse',   badge: 'bg-blue-50 text-blue-600 border-blue-100' },
};

const MGMT_META = {
  [MGMT_STATUSES.KEEP]:             { label: 'Keep',             className: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  [MGMT_STATUSES.REVIEW]:           { label: 'Review',           className: 'text-amber-700 bg-amber-50 border-amber-200' },
  [MGMT_STATUSES.REMOVE_CANDIDATE]: { label: 'Remove Candidate', className: 'text-red-700 bg-red-50 border-red-200' },
};

const FILTER_OPTIONS = [
  { key: 'all',       label: 'All' },
  { key: 'favorites', label: '❤️ Favorites' },
  { key: 'active',    label: '🟢 Active' },
  { key: 'review',    label: '🟡 Review' },
  { key: 'inactive',  label: '⚪ Inactive' },
  { key: 'attention', label: '⚠️ Needs Attention' },
];

function timeAgo(dateInput) {
  if (!dateInput) return null;
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return null;
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay === 1) return '1 day ago';
  if (diffDay < 30) return `${diffDay} days ago`;
  const diffMonth = Math.round(diffDay / 30);
  if (diffMonth < 12) return `${diffMonth}mo ago`;
  const diffYear = Math.round(diffMonth / 12);
  return `${diffYear}y ago`;
}

function formatShortDate(dateInput) {
  if (!dateInput) return null;
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatScrapedLabel(dateInput) {
  if (!dateInput) return 'Never';
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return 'Never';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Today';
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return formatShortDate(d);
}

// Runs `worker` over `items` with a max of `limit` in flight at once.
async function runWithConcurrency(items, worker, limit) {
  let idx = 0;
  async function lane() {
    while (idx < items.length) {
      const item = items[idx++];
      await worker(item);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, lane));
}

function StatPill({ label, value, dotClass, icon, className = '' }) {
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${className}`}>
      {dotClass && <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotClass}`} />}
      {icon}
      <span className="font-semibold">{value}</span>
      <span className="text-xs opacity-80">{label}</span>
    </div>
  );
}

function SourceCard({ source, stats, mgmtStatus, isFavorite, lastViewedAt, onView, onToggleFavorite, onDelete, onMgmtChange }) {
  const logoUrl = getWebsiteLogo(source?.websiteUrl);
  const activity = getActivityStatus(stats);
  const activityMeta = ACTIVITY_META[activity];
  const scrapedLabel = formatScrapedLabel(stats?.checkedAt || source?.lastScrapedAt);
  const latestLabel = formatShortDate(stats?.latestArticleDate);
  const latestRelative = timeAgo(stats?.latestArticleDate);
  const viewedRelative = timeAgo(lastViewedAt);
  const articlesCount = stats?.articlesCount;

  return (
    <div className="group bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 p-5 flex flex-col">
      {/* Top row */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 w-11 h-11 bg-white border border-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>';
                }}
              />
            ) : (
              <Globe className="w-5 h-5 text-blue-600" />
            )}
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">{source?.websiteName}</h3>
            <p className="text-xs text-gray-500 truncate">{source?.websiteUrl}</p>
          </div>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            onClick={() => onToggleFavorite(source?.id)}
            className="p-1.5 rounded-md hover:bg-pink-50 transition-colors"
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Heart className="w-4 h-4" fill={isFavorite ? '#ef4444' : 'none'} stroke={isFavorite ? '#ef4444' : '#9ca3af'} />
          </button>
          <button
            onClick={() => onDelete(source?.id)}
            className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            title="Delete Website"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Activity status badge */}
      <div className={`inline-flex items-center gap-1.5 self-start px-2 py-1 rounded-full border text-[11px] font-semibold tracking-wide mb-3 ${activityMeta.badge}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${activityMeta.dot}`} />
        {activityMeta.label}
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 text-xs mb-4">
        <div>
          <div className="text-gray-400 mb-0.5">Latest article</div>
          {latestLabel ? (
            <div className="font-medium text-gray-800">
              {latestLabel}
              <span className="block text-gray-400 font-normal">{latestRelative}</span>
            </div>
          ) : (
            <div className="font-medium text-gray-800">
              {stats?.checkedAt ? 'No articles' : <span className="text-gray-300">Checking…</span>}
            </div>
          )}
        </div>
        <div>
          <div className="text-gray-400 mb-0.5">Last scraped</div>
          <div className="font-medium text-gray-800">{scrapedLabel}</div>
        </div>
        <div>
          <div className="text-gray-400 mb-0.5">Articles</div>
          <div className="font-medium text-gray-800">
            {articlesCount != null
              ? `${articlesCount}${stats?.hasMore ? '+' : ''} article${articlesCount === 1 && !stats?.hasMore ? '' : 's'}`
              : '—'}
          </div>
        </div>
        <div>
          <div className="text-gray-400 mb-0.5">Last viewed</div>
          <div className="font-medium text-gray-800">{viewedRelative || 'Never'}</div>
        </div>
      </div>

      {/* Management status — independent of the Favorite heart, never deletes anything */}
      <div className="mb-3">
        <label className="block text-[11px] text-gray-400 mb-1">Status</label>
        <div className={`relative rounded-lg border text-xs font-medium ${MGMT_META[mgmtStatus]?.className || MGMT_META[MGMT_STATUSES.KEEP].className}`}>
          <select
            value={mgmtStatus}
            onChange={(e) => onMgmtChange(source?.id, e.target.value)}
            className="w-full appearance-none bg-transparent px-2.5 py-1.5 pr-7 rounded-lg focus:outline-none cursor-pointer"
          >
            <option value={MGMT_STATUSES.KEEP}>✓ Keep</option>
            <option value={MGMT_STATUSES.REVIEW}>! Review</option>
            <option value={MGMT_STATUSES.REMOVE_CANDIDATE}>× Remove Candidate</option>
          </select>
          <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
        </div>
      </div>

      <Button onClick={() => onView(source)} className="w-full bg-sky-400 hover:bg-sky-500 text-white mt-auto">
        <Eye className="w-4 h-4 mr-2" />
        View Articles
      </Button>
    </div>
  );
}

// Main CopyBlogs Component
export default function CopyBlogs() {
  const navigate = useNavigate();
  const { alert, confirm } = useModal();
  const { activeProfile } = useLanguageProfile();
  const startSourcesRequest = useRequestGuard();
  const startStatsRequest = useRequestGuard();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newWebsiteUrl, setNewWebsiteUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [showArticlesPanel, setShowArticlesPanel] = useState(false);
  const [savedArticles, setSavedArticles] = useState([]);
  const [favorites, setFavorites] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('copyblogs_favorites') || '[]'));
    } catch { return new Set(); }
  });

  // Dashboard-only state — management status, last-viewed, and activity stats.
  // Persisted to localStorage (see utils/copyBlogsSourceMeta), never to
  // copy_blogs_sources, so the scraping/article data model is untouched.
  const [mgmtStatusMap, setMgmtStatusMap] = useState(() => loadMgmtStatusMap());
  const [lastViewedMap, setLastViewedMap] = useState(() => loadLastViewedMap());
  const [statsMap, setStatsMap] = useState(() => loadStatsCache());

  const [search, setSearch] = useState('');
  const [filterKey, setFilterKey] = useState('all');
  const [sortKey, setSortKey] = useState('latest_article');

  // Clear the previous profile's sources immediately on switch so stale
  // content can't flash while the new profile's data is loading.
  useEffect(() => {
    setSources([]);
  }, [activeProfile?.id]);

  useEffect(() => {
    loadSources();
  }, [activeProfile?.id]);

  const loadSources = async () => {
    const isCurrent = startSourcesRequest();
    setLoading(true);
    try {
      const data = await getCopyBlogsSources(activeProfile?.id);
      if (!isCurrent()) return;
      setSources(data || []);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error loading sources:', error);
      alert(`Error loading sources: ${error?.message}`);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  // Background: quietly refresh activity stats (latest article date, article
  // count) for any source whose cached stats are missing or stale, using the
  // exact same fetchArticlesFast() the article panel already uses. Throttled
  // to a few sites at a time so it never hammers the sources list.
  useEffect(() => {
    if (!sources || sources.length === 0) return;
    const isCurrent = startStatsRequest();

    const toRefresh = sources.filter((s) => {
      const cached = statsMap?.[s?.id];
      return !cached?.checkedAt || (Date.now() - new Date(cached.checkedAt).getTime()) > STATS_TTL_MS;
    });
    if (toRefresh.length === 0) return;

    runWithConcurrency(toRefresh, async (source) => {
      if (!isCurrent()) return;
      let entry;
      try {
        const data = await fetchArticlesFast(source?.websiteUrl, FETCH_LIMIT, 1);
        const sorted = sortByDate(data || []);
        const more = (data || []).length === FETCH_LIMIT;
        _articlesCache[source?.websiteUrl] = { articles: sorted, page: 1, hasMore: more, fetchedAt: Date.now() };
        entry = {
          latestArticleDate: sorted?.[0]?.publishDate || null,
          articlesCount: sorted.length,
          hasMore: more,
          checkedAt: new Date().toISOString(),
        };
      } catch {
        entry = { latestArticleDate: null, articlesCount: 0, hasMore: false, checkedAt: new Date().toISOString() };
      }
      if (!isCurrent()) return;
      saveStatsEntry(source?.id, entry);
      setStatsMap((prev) => ({ ...prev, [source?.id]: { ...prev?.[source?.id], ...entry } }));
    }, STATS_CONCURRENCY);
  }, [sources]);

  const handleAddSource = async (e) => {
    e?.preventDefault();

    if (!newWebsiteUrl?.trim()) {
      alert('Please provide a website URL');
      return;
    }

    setAdding(true);
    try {
      // Normalize URL before adding
      const normalized = normalizeWebsiteUrl(newWebsiteUrl);
      await addCopyBlogsSource(normalized, activeProfile?.id || null);
      setNewWebsiteUrl('');
      setShowAddForm(false);
      await loadSources();
    } catch (error) {
      console.error('Error adding source:', error);
      alert(`Error adding website: ${error?.message}`);
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteSource = async (sourceId) => {
    if (!(await confirm('Are you sure you want to delete this website? All scraped articles will be removed.'))) {
      return;
    }

    try {
      // Clear persisted pagination state
      localStorage.removeItem(`copyblogs_${sourceId}_pagination`);

      await deleteCopyBlogsSource(sourceId);
      await loadSources();
    } catch (error) {
      console.error('Error deleting source:', error);
      alert(`Error deleting website: ${error?.message}`);
    }
  };

  const handleViewArticles = (source) => {
    setSelectedSource(source);
    setShowArticlesPanel(true);
    recordLastViewed(source?.id);
    setLastViewedMap((prev) => ({ ...prev, [source?.id]: new Date().toISOString() }));
  };

  const handleToggleFavorite = (sourceId) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(sourceId)) {
        next.delete(sourceId);
      } else {
        next.add(sourceId);
      }
      try {
        localStorage.setItem('copyblogs_favorites', JSON.stringify([...next]));
      } catch {}
      return next;
    });
  };

  const handleMgmtStatusChange = (sourceId, status) => {
    saveMgmtStatus(sourceId, status);
    setMgmtStatusMap((prev) => ({ ...prev, [sourceId]: status }));
  };

  const handleArticlesLoadedFromPanel = (source, entry) => {
    if (!source?.id) return;
    saveStatsEntry(source.id, entry);
    setStatsMap((prev) => ({ ...prev, [source.id]: { ...prev?.[source.id], ...entry } }));
  };

  const handleAddArticleToAutomation = async (article) => {
    setSavedArticles(prev => [...prev, article]);

    // Persist to localStorage so AddBlogPost can pick them up on navigation.
    // Scoped to the currently active profile so an article saved under one
    // language profile never shows up under another.
    try {
      const key = getCopyBlogsQueueKey(activeProfile?.id);
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      existing.push({
        title:             article?.title || '',
        focusKeyword:      article?.focusKeyword || '',
        shortDescription:  article?.excerpt || '',
        referenceImageUrl: article?.featuredImageUrl || '',
        category:          article?.category || '',
        sourceArticleUrl:  article?.articleUrl || '',
      });
      localStorage.setItem(key, JSON.stringify(existing));
    } catch {}
  };

  // Dashboard-wide metrics (header pills + filter chip counts) computed from
  // the same activity stats / management status used per card.
  const metrics = useMemo(() => {
    let favoritesN = 0, activeN = 0, reviewN = 0, inactiveN = 0, attentionN = 0;
    (sources || []).forEach((s) => {
      const stats = statsMap?.[s?.id];
      const activity = getActivityStatus(stats);
      const mgmt = mgmtStatusMap?.[s?.id] || MGMT_STATUSES.KEEP;
      if (favorites.has(s?.id)) favoritesN++;
      if (activity === 'active') activeN++;
      if (activity === 'getting_old' || mgmt === MGMT_STATUSES.REVIEW) reviewN++;
      if (activity === 'inactive' || mgmt === MGMT_STATUSES.REMOVE_CANDIDATE) inactiveN++;
      if (isNeedsAttention(stats, mgmt)) attentionN++;
    });
    return {
      total: sources?.length || 0,
      favorites: favoritesN,
      active: activeN,
      review: reviewN,
      inactive: inactiveN,
      attention: attentionN,
    };
  }, [sources, statsMap, mgmtStatusMap, favorites]);

  // "Continue where you left off" — most recently viewed sources still present
  const continueWorking = useMemo(() => {
    return Object.entries(lastViewedMap || {})
      .map(([sourceId, ts]) => ({ source: (sources || []).find((s) => s?.id === sourceId), ts }))
      .filter((x) => x.source)
      .sort((a, b) => new Date(b.ts) - new Date(a.ts))
      .slice(0, 3);
  }, [lastViewedMap, sources]);

  // Search + filter + sort pipeline for the sources grid
  const visibleSources = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = (sources || []).filter((s) => {
      if (!q) return true;
      return s?.websiteName?.toLowerCase()?.includes(q) || s?.websiteUrl?.toLowerCase()?.includes(q);
    });

    list = list.filter((s) => {
      const stats = statsMap?.[s?.id];
      const activity = getActivityStatus(stats);
      const mgmt = mgmtStatusMap?.[s?.id] || MGMT_STATUSES.KEEP;
      switch (filterKey) {
        case 'favorites': return favorites.has(s?.id);
        case 'active':    return activity === 'active';
        case 'review':    return activity === 'getting_old' || mgmt === MGMT_STATUSES.REVIEW;
        case 'inactive':  return activity === 'inactive' || mgmt === MGMT_STATUSES.REMOVE_CANDIDATE;
        case 'attention': return isNeedsAttention(stats, mgmt);
        default:          return true;
      }
    });

    const timeOf = (v) => (v ? new Date(v).getTime() : null);

    list = [...list].sort((a, b) => {
      const sa = statsMap?.[a?.id];
      const sb = statsMap?.[b?.id];
      switch (sortKey) {
        case 'oldest_article': {
          const ta = timeOf(sa?.latestArticleDate), tb = timeOf(sb?.latestArticleDate);
          if (ta == null && tb == null) return 0;
          if (ta == null) return 1;
          if (tb == null) return -1;
          return ta - tb;
        }
        case 'recently_viewed': {
          const ta = timeOf(lastViewedMap?.[a?.id]), tb = timeOf(lastViewedMap?.[b?.id]);
          if (ta == null && tb == null) return 0;
          if (ta == null) return 1;
          if (tb == null) return -1;
          return tb - ta;
        }
        case 'recently_scraped': {
          const ta = timeOf(sa?.checkedAt || a?.lastScrapedAt), tb = timeOf(sb?.checkedAt || b?.lastScrapedAt);
          if (ta == null && tb == null) return 0;
          if (ta == null) return 1;
          if (tb == null) return -1;
          return tb - ta;
        }
        case 'name':
          return (a?.websiteName || '').localeCompare(b?.websiteName || '');
        case 'latest_article':
        default: {
          const ta = timeOf(sa?.latestArticleDate), tb = timeOf(sb?.latestArticleDate);
          if (ta == null && tb == null) return 0;
          if (ta == null) return 1;
          if (tb == null) return -1;
          return tb - ta;
        }
      }
    });

    return list;
  }, [sources, search, filterKey, sortKey, statsMap, mgmtStatusMap, favorites, lastViewedMap]);

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50">
        <Sidebar />
        <main className="main-content">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-start justify-between mb-4 gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Copy Blogs</h1>
                <p className="mt-1 text-gray-600 text-sm sm:text-base">
                  Manage and monitor your content sources
                </p>
              </div>
              <Button
                onClick={() => navigate('/wp/add-post')}
                variant="outline"
                className="ml-4 flex-shrink-0 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span className="hidden sm:inline">Go to Add Blog Post</span>
                <span className="sm:hidden">Add Post</span>
              </Button>
            </div>

            {/* Dashboard stats */}
            <div className="flex flex-wrap gap-2 mb-5">
              <StatPill label="Sources" value={metrics.total} className="bg-white border-gray-200 text-gray-700" />
              <StatPill
                label="Favorites"
                value={metrics.favorites}
                icon={<Heart className="w-3.5 h-3.5" fill="#ec4899" stroke="#ec4899" />}
                className="bg-pink-50 border-pink-100 text-pink-700"
              />
              <StatPill label="Active" value={metrics.active} dotClass="bg-emerald-500" className="bg-emerald-50 border-emerald-100 text-emerald-700" />
              <StatPill label="Need Review" value={metrics.review} dotClass="bg-amber-500" className="bg-amber-50 border-amber-100 text-amber-700" />
              <StatPill label="Inactive" value={metrics.inactive} dotClass="bg-gray-400" className="bg-gray-100 border-gray-200 text-gray-600" />
            </div>

            {/* Continue where you left off */}
            {continueWorking.length > 0 && (
              <div className="mb-5">
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <History className="w-3.5 h-3.5" />
                  Continue where you left off
                </h2>
                <div className="flex flex-wrap gap-2">
                  {continueWorking.map(({ source, ts }) => (
                    <button
                      key={source.id}
                      onClick={() => handleViewArticles(source)}
                      className="inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 rounded-lg px-3 py-2 text-sm transition-colors"
                    >
                      <Globe className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                      <span className="font-medium text-gray-800 truncate max-w-[10rem]">{source.websiteName}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">viewed {timeAgo(ts)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Smart management toolbar */}
            <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 mb-5 flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search sources..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex flex-wrap gap-1.5">
                {FILTER_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setFilterKey(opt.key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors whitespace-nowrap ${
                      filterKey === opt.key
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {opt.label}
                    {opt.key !== 'all' ? ` (${metrics[opt.key] ?? 0})` : ''}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                  className="text-xs sm:text-sm border border-gray-200 rounded-lg px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="latest_article">Sort: Latest Article</option>
                  <option value="oldest_article">Sort: Oldest Article</option>
                  <option value="recently_viewed">Sort: Recently Viewed</option>
                  <option value="recently_scraped">Sort: Recently Scraped</option>
                  <option value="name">Sort: Website Name</option>
                </select>

                <Button onClick={() => setShowAddForm(!showAddForm)} className="flex items-center flex-shrink-0">
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Add Website
                </Button>
              </div>
            </div>

            {/* Add Website Form */}
            {showAddForm && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Add External Website</h2>
                <form onSubmit={handleAddSource} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Website URL *
                    </label>
                    <Input
                      value={newWebsiteUrl}
                      onChange={(e) => setNewWebsiteUrl(e?.target?.value)}
                      placeholder="example.com or https://example.com"
                      type="text"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Enter with or without https:// - we'll normalize it automatically
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Button type="submit" disabled={adding}>
                      {adding ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Adding...
                        </>
                      ) : (
                        'Add Website'
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowAddForm(false);
                        setNewWebsiteUrl('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Sources Grid */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : sources?.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-lg border-2 border-dashed border-gray-300">
                <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Websites Added</h3>
                <p className="text-gray-600 mb-6">Add your first external website to start browsing blog posts</p>
                <Button onClick={() => setShowAddForm(true)}>
                  <PlusCircle className="w-5 h-5 mr-2" />
                  Add Website
                </Button>
              </div>
            ) : visibleSources.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-lg border-2 border-dashed border-gray-300">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No sources match your filters</h3>
                <p className="text-gray-600 mb-6">Try a different search term or filter.</p>
                <Button
                  variant="outline"
                  onClick={() => { setSearch(''); setFilterKey('all'); }}
                >
                  Clear filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {visibleSources.map((source) => (
                  <SourceCard
                    key={source?.id}
                    source={source}
                    stats={statsMap?.[source?.id]}
                    mgmtStatus={mgmtStatusMap?.[source?.id] || MGMT_STATUSES.KEEP}
                    isFavorite={favorites.has(source?.id)}
                    lastViewedAt={lastViewedMap?.[source?.id]}
                    onView={handleViewArticles}
                    onToggleFavorite={handleToggleFavorite}
                    onDelete={handleDeleteSource}
                    onMgmtChange={handleMgmtStatusChange}
                  />
                ))}
              </div>
            )}
          </div>
        </main>

        {/* Articles Side Panel */}
        <ArticlesPanel
          source={selectedSource}
          isOpen={showArticlesPanel}
          onClose={() => {
            setShowArticlesPanel(false);
            setSelectedSource(null);
          }}
          onAddToAutomation={handleAddArticleToAutomation}
          onArticlesLoaded={handleArticlesLoadedFromPanel}
        />
      </div>
    </SidebarProvider>
  );
}
